import { DemoDialog } from 'app/src/features/DemoApp'
import { BuildBeneficiaries } from 'app/src/features/Shopping/shared/utils'
import type { shoppingApi } from 'app/src/services/welfareportal'
import { getValueFromTypology, isDemoApp } from 'app/src/shared/helpers'
import { Button } from 'primereact/button'
import { Image } from 'primereact/image'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function DiscountWidget(props: { order: shoppingApi.OrderDetails }) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct' })
  const { t: tProductDetail } = useTranslation('productDetail')
  const [showDemoDialog, setShowDemoDialog] = useState(false)
  const orderDetailDiscount = props.order?.purchaseDetails as shoppingApi.OrderDetailsPurchaseDiscount

  const getDiscountLabel = (discountType?: string): string => {
    if (discountType === 'percentage') {
      return tProductDetail('ProductDetail.discount.percentage')
    } else if (discountType === 'fixed') {
      return tProductDetail('ProductDetail.discount.fixed')
    }
    return tProductDetail('ProductDetail.discount.generic')
  }

  const amount = useMemo(() => {
    if (!props.order?.variant) return '-'

    const discountType = props.order.variant?.priceDetails?.discountType
    const discountValue = props.order.variant?.priceDetails?.discountValue

    return discountValue ? getValueFromTypology(discountType, discountValue) : props.order?.variant?.variantName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, props.order])

  const beneficiaries = useMemo(() => BuildBeneficiaries(props.order), [props.order])

  const handleOpenUrl = (url: string) => {
    if (isDemoApp()) {
      setShowDemoDialog(true)
    } else {
      window.open(url, '_blank')
    }
  }

  return (
    orderDetailDiscount && (
      <>
        <DemoDialog visible={showDemoDialog} onHide={() => setShowDemoDialog(false)} />
        <div className='printable card grid grid-cols-1 divide-x-0 divide-y-2 divide-dotted rounded-xl border border-solid border-[var(--primary-color)] bg-[var(--primary-color)] p-5 text-white lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
          <div className='lg:min-w[300px] col-span-12 p-2 text-center lg:col-span-4 lg:pr-5'>
            <div className='mb-4' dangerouslySetInnerHTML={{ __html: t('Common.pwsLogo') }} />
            {props.order.status === 'canceled' ? (
              <></>
            ) : orderDetailDiscount?.discountActivationUrl ? (
              <>
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('Discount.discountToActivate.header', { PRODUCTNAME: props.order.product.productName }),
                  }}
                />
                <div className='print:hidden'>
                  <Button
                    onClick={() => handleOpenUrl(orderDetailDiscount.discountActivationUrl!)}
                    rounded
                    pt={{ root: { className: 'rounded-3xl bg-[white] text-[var(--primary-color)] mt-4' } }}
                  >
                    {t('Discount.discountToActivate.activateDiscountButtonLabel')}
                  </Button>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('Discount.discountToActivate.trailer', { PRODUCTNAME: props.order.product.productName }),
                  }}
                  className='print:hidden'
                />
              </>
            ) : (
              <>
                {orderDetailDiscount?.discountCode && (
                  <>
                    <div
                      className='mb-1'
                      dangerouslySetInnerHTML={{
                        __html: t('Discount.discountActive.mainHeader', { PRODUCTNAME: props.order.product.productName }),
                      }}
                    />
                    <div className='text-base font-bold'>
                      <span>{orderDetailDiscount.discountCode}</span>
                    </div>
                  </>
                )}
                {orderDetailDiscount?.discountDetailsUrl && (
                  <div className='mt-2 print:hidden'>
                    <Button
                      onClick={() => handleOpenUrl(orderDetailDiscount.discountDetailsUrl!)}
                      rounded
                      pt={{ root: { className: 'rounded-3xl bg-[white] text-[var(--primary-color)] mt-4' } }}
                    >
                      {t('Discount.discountActive.getYourDiscountButtonLabel')}
                    </Button>
                  </div>
                )}
              </>
            )}
            {orderDetailDiscount?.voucherImageUrl && (
              <div className='mx-auto mb-4 max-h-fit max-w-fit rounded-xl border bg-white p-[2px]'>
                <Image
                  alt="Immagine del voucher per l'utilizzo del bene o servizio"
                  pt={{ image: { className: 'max-w-[220px] max-h-[220px]' } }}
                  src={orderDetailDiscount?.voucherImageUrl}
                />
              </div>
            )}
            {orderDetailDiscount?.voucherCode && (
              <>
                <div
                  className='mb-1'
                  dangerouslySetInnerHTML={{
                    __html: t('Discount.voucherCodeDescription'),
                  }}
                />
                <div className='text-base font-bold'>
                  <span>{orderDetailDiscount.voucherCode}</span>
                </div>
              </>
            )}
          </div>
          <div className='lg:min-w[500px] col-span-12 mx-auto w-full p-2 lg:col-span-8 lg:pl-5'>
            <div className='mt-6 lg:mt-0'>{t('Discount.orderIdLabel')}</div>
            <div className='mb-4 w-full break-words pt-2 font-bold uppercase lg:mb-0'>{props.order?.orderId ?? '-'}</div>
            <div className='mb-4 mt-2 hidden h-px w-full bg-white lg:block'></div>
            <div className='grid grid-cols-12'>
              <div className='col-span-12 mb-4 mt-2 lg:col-span-4 lg:my-0'>
                <div>
                  {props.order.variant?.priceDetails?.discountType
                    ? getDiscountLabel(props.order.variant.priceDetails.discountType)
                    : t('Discount.priceDescription')}
                </div>
                <div className='pt-2 font-bold uppercase'>{amount}</div>
              </div>
              <div className='col-span-12 mt-2 lg:col-span-4 lg:col-start-7 lg:mt-0'>
                <div>{t('Discount.buyerDescription')}</div>
                <div className='pt-2 font-bold uppercase'>{beneficiaries}</div>
              </div>
            </div>
          </div>
        </div>
        {props.order?.status === 'canceled' || orderDetailDiscount?.discountDetailsUrl ? (
          <></>
        ) : (
          <div className='mt-4 flex justify-center print:hidden'>
            <Button role='link' rounded outlined onClick={() => window.print()} link>
              {t('Discount.getYourDiscountButtonLabel')}
            </Button>
          </div>
        )}
      </>
    )
  )
}
